import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, ExternalLink, Filter, Search, ChevronLeft, ChevronRight, Sparkles, Building2 } from 'lucide-react';
import { Publication } from '@ftt/shared';

const ITEMS_PER_PAGE = 18;

export const PublicationsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedPublisher, setSelectedPublisher] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: publicationsRes, isLoading } = useQuery({
    queryKey: ['public-publications'],
    queryFn: () => api.get('/public/publications').then((res) => res.data.data),
  });

  const publications: Publication[] = publicationsRes || [];

  // Filtered publications
  const filtered = useMemo(() => {
    return publications.filter((book) => {
      if (selectedCategory !== 'ALL' && !book.category?.toLowerCase().includes(selectedCategory.toLowerCase())) {
        return false;
      }
      if (selectedPublisher !== 'ALL' && book.publisher !== selectedPublisher) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchTitle = book.title?.toLowerCase().includes(q);
        const matchAuthor = book.author?.toLowerCase().includes(q);
        const matchDesc = book.description?.toLowerCase().includes(q);
        const matchCat = book.category?.toLowerCase().includes(q);
        const matchPub = book.publisher?.toLowerCase().includes(q);
        if (!matchTitle && !matchAuthor && !matchDesc && !matchCat && !matchPub) {
          return false;
        }
      }
      return true;
    });
  }, [publications, selectedCategory, selectedPublisher, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const paginatedBooks = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handlePublisherChange = (pub: string) => {
    setSelectedPublisher(pub);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-12 py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans antialiased text-slate-900">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-xs font-bold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5 text-brand-600" />
          <span>Official Catalog &bull; FOR THE TRUTH PRESS</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-black text-slate-900 tracking-tight">
          Literature of Enduring Truth
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium">
          Explore {publications.length || 432} published theological titles, biblical commentaries, puritan classics, and vernacular translations from <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong>.
        </p>
      </section>

      {/* Search & Filter Controls */}
      <section className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        
        {/* Search Input */}
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by title, author, keywords, or publisher..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-900 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:bg-white transition-all"
            />
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-slate-700 shrink-0 self-end md:self-auto">
            <span>Showing <strong className="text-slate-900">{filtered.length}</strong> of {publications.length || 432} titles</span>
          </div>
        </div>

        {/* Categories Chips */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mr-1">Category:</span>
          {[
            { id: 'ALL', label: 'All Categories' },
            { id: 'Bible Study', label: 'Bible Study' },
            { id: 'Children', label: 'Children & Family' },
            { id: 'Theology', label: 'Theology' },
            { id: 'Christian Living', label: 'Christian Living' },
            { id: 'Church Questions', label: 'Church Questions' },
            { id: 'Apologetics', label: 'Apologetics' },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Publisher Filter */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mr-1">Publisher:</span>
          {[
            { id: 'ALL', label: 'All Publishers' },
            { id: 'FOR THE TRUTH', label: 'FOR THE TRUTH' },
            { id: 'THE GOOD BOOK COMPANY', label: 'The Good Book Company' },
            { id: 'SHEPHERD PRESS', label: 'Shepherd Press' },
            { id: '10PUBLISHING', label: '10Publishing' },
            { id: 'GOOD SHEPHERD BOOKS', label: 'Good Shepherd Books' },
          ].map((pub) => (
            <button
              key={pub.id}
              onClick={() => handlePublisherChange(pub.id)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                selectedPublisher === pub.id
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {pub.label}
            </button>
          ))}
        </div>

      </section>

      {/* Catalog Grid */}
      <section>
        {isLoading ? (
          <div className="text-center py-20 text-slate-500 font-mono text-xs">Loading publications catalog...</div>
        ) : paginatedBooks.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 text-slate-600 text-sm font-medium">
            No publications found matching your search. Try resetting filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedBooks.map((book) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300"
              >
                {/* Book Cover Image Area */}
                <div className="aspect-4/5 bg-slate-50/90 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-100">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-slate-900/90 backdrop-blur-xs text-white text-[9px] font-mono px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                    {book.format}
                  </div>
                  {book.publisher && (
                    <div className="absolute bottom-2 right-2 bg-white/95 text-slate-800 text-[9px] font-bold px-2 py-0.5 rounded shadow-2xs border border-slate-200">
                      {book.publisher}
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="p-5 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-brand-700 font-mono font-bold uppercase tracking-wider block">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-lg font-extrabold text-slate-900 group-hover:text-brand-700 transition-colors line-clamp-2 leading-snug" title={book.title}>
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-600 font-bold">
                      By <span className="text-slate-900">{book.author}</span>
                    </p>
                    <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed pt-1 font-medium">
                      {book.description}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="text-brand-700 hover:text-brand-900 flex items-center gap-1"
                    >
                      <span>Book Details</span>
                      <span>&rarr;</span>
                    </Link>

                    {book.purchaseUrl && (
                      <a
                        href={book.purchaseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors text-[11px] font-extrabold"
                      >
                        <span>Buy on Store</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <section className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="text-xs font-bold text-slate-600">
            Page <strong className="text-slate-900">{currentPage}</strong> of <strong className="text-slate-900">{totalPages}</strong> ({filtered.length} Titles)
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3.5 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors cursor-pointer"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

    </div>
  );
};
